import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Lock, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtD } from "@/lib/pipelineApi";
import {
  getHorseAccounting, updateFee, saveSale, addExpense, editExpense, deleteExpense, INVOICE_LABEL, PAYMENT_LABEL, GST_LABEL, SOURCE_LABEL, CATEGORY_LABEL, money, errText,
} from "@/lib/accountingApi";

const L = "block text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1";
const SEL = "w-full h-10 px-3 rounded-md border border-stone-200 bg-white text-sm disabled:opacity-60";
const d8 = (v) => (v ? fmtD(v) : "—");
const today = () => new Date().toLocaleDateString("en-CA");
const MANUAL = ["creating", "invoiced", "error", "voided"];

function Row({ k, v, testId, strong }) {
  return <div className="flex justify-between gap-3 text-sm py-1.5 border-b border-stone-100 last:border-0"><span className="text-stone-600">{k}</span><span data-testid={testId} className={`text-right break-words ${strong ? "font-bold" : "font-medium"} text-stone-900`}>{v ?? "—"}</span></div>;
}

function Section({ title, testId, action, children }) {
  return (
    <section data-testid={testId} className="bg-white border border-stone-200 rounded-xl p-4">
      <div className="flex items-center justify-between gap-2 mb-2"><h3 className="font-semibold text-stone-900">{title}</h3>{action}</div>
      {children}
    </section>
  );
}

function Notes({ list, testId }) {
  if (!list?.length) return null;
  return <ul data-testid={testId} className="mt-2 space-y-1">{list.map((n) => <li key={n} className="flex gap-1.5 text-xs text-orange-900"><AlertCircle className="h-3.5 w-3.5 shrink-0 mt-px" />{n}</li>)}</ul>;
}

function Field({ label, wide, children }) {
  return <div className={wide ? "sm:col-span-2" : ""}><label className={L}>{label}</label>{children}</div>;
}

// Saves on blur / Enter, only when the value changed.
function SaveInput({ value, onSave, onSkip, testId, ...p }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  const commit = () => { if (String(v) !== String(value ?? "")) onSave(v); else onSkip?.(); };
  return <Input data-testid={testId} value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} {...p} />;
}

function InvoiceFields({ fee, t, off, save }) {
  const manual = MANUAL.includes(fee.invoice_status);
  const pick = (s) => {
    if (s === (manual ? fee.invoice_status : "auto")) return;
    const body = { invoice_status: s };
    if (s === "invoiced" && !fee.invoice_date) body.invoice_date = today();
    save(body);
  };
  return <>
    <Field label="Invoice status">
      <select data-testid={`${t}-invoice-status`} className={SEL} disabled={off} value={manual ? fee.invoice_status : "auto"} onChange={(e) => pick(e.target.value)}>
        <option value="auto">{manual ? "Reset to calculated status" : INVOICE_LABEL[fee.invoice_status]}</option>
        {MANUAL.map((k) => <option key={k} value={k}>{INVOICE_LABEL[k]}</option>)}
      </select></Field>
    <Field label="QBO invoice #"><SaveInput testId={`${t}-qbo-number`} disabled={off} value={fee.qbo_invoice_number} onSave={(v) => save({ qbo_invoice_number: v })} /></Field>
    <Field label="QBO invoice ID"><SaveInput testId={`${t}-qbo-id`} disabled={off} value={fee.qbo_invoice_id} onSave={(v) => save({ qbo_invoice_id: v })} /></Field>
    <Field label="Invoice date"><SaveInput testId={`${t}-invoice-date`} type="date" disabled={off} value={fee.invoice_date} onSave={(v) => save({ invoice_date: v })} /></Field>
    <Field label="Due date"><SaveInput testId={`${t}-due-date`} type="date" disabled={off} value={fee.due_date} onSave={(v) => save({ due_date: v })} /></Field>
  </>;
}

function PaymentFields({ fee, t, off, save }) {
  const [partial, setPartial] = useState(false);
  useEffect(() => { if (partial) document.querySelector(`[data-testid="${t}-amount-paid"]`)?.focus(); }, [partial, t]);
  if (fee.invoice_status !== "invoiced") return <Field label="Payment status" wide><p data-testid={`${t}-payment-status`} className="text-sm text-stone-600 py-2">— (recorded once the fee is Invoiced)</p></Field>;
  const pick = (ps) => {
    if (ps === "partially_paid") { setPartial(true); return; }
    setPartial(false);
    const body = { payment_status: ps };
    if (ps === "paid" && !fee.payment_date) body.payment_date = today();
    if (ps === "unpaid") body.amount_paid = 0;
    save(body);
  };
  return <>
    <Field label="Payment status">
      <select data-testid={`${t}-payment-status`} className={SEL} disabled={off} value={partial ? "partially_paid" : fee.payment_status} onChange={(e) => pick(e.target.value)}>
        {Object.entries(PAYMENT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select></Field>
    <Field label={`Amount paid${fee.total != null ? ` (of ${money(fee.total, fee.currency)})` : ""}`}>
      <SaveInput testId={`${t}-amount-paid`} type="number" min="0" step="0.01" disabled={off} onSkip={() => setPartial(false)} placeholder={partial ? "Enter amount, then press Enter" : ""}
        value={fee.amount_paid || ""} onSave={(v) => { setPartial(false); save({ payment_status: partial ? "partially_paid" : fee.payment_status, amount_paid: Number(v || 0) }); }} /></Field>
    <Field label="Payment date"><SaveInput testId={`${t}-payment-date`} type="date" disabled={off} value={fee.payment_date} onSave={(v) => save({ payment_date: v })} /></Field>
  </>;
}

function PricingFields({ fee, t, off, save, sale }) {
  const initial = fee.fee_type === "initial", lock = off || fee.locked;
  return <>
    {!initial && <>
      <Field label="Sale outcome">
        <select data-testid={`${t}-outcome`} className={SEL} disabled={lock} value={fee.sale_outcome || ""} onChange={(e) => sale({ outcome: e.target.value })}>
          {!fee.sale_outcome && <option value="" disabled>Not recorded</option>}<option value="sold">Sold</option><option value="unsold">Not Sold</option><option value="withdrawn">Withdrawn</option>
        </select></Field>
      <Field label={`Confirmed sale price${fee.currency ? ` (${fee.currency})` : ""}`}><SaveInput testId={`${t}-sale-price`} type="number" min="0" step="0.01" disabled={lock || fee.sale_outcome !== "sold"} value={fee.sale_price} onSave={(v) => sale({ sale_price: v === "" ? null : Number(v) })} /></Field>
      <Field label="Sale date"><SaveInput testId={`${t}-sale-date`} type="date" disabled={lock || !fee.sale_outcome} value={fee.sale_date} onSave={(v) => sale({ sold_at: v || undefined })} /></Field>
      <Field label="Sale price source">
        <select data-testid={`${t}-source`} className={SEL} disabled={lock} value={fee.sale_price_source} onChange={(e) => save({ sale_price_source: e.target.value })}>
          {Object.entries(SOURCE_LABEL).map(([k, v]) => <option key={k} value={k} disabled={k !== "manual"}>{v}{k !== "manual" ? " (coming soon)" : ""}</option>)}
        </select></Field>
    </>}
    {initial && <Field label="Weekly rate override"><SaveInput testId={`${t}-rate-override`} type="number" min="0" step="0.01" disabled={lock} placeholder={fee.rate != null ? String(fee.rate) : ""} value={fee.overrides.rate_override}
      onSave={(v) => (v === "" ? save({ clear_rate_override: true }) : save({ rate_override: Number(v) }))} /></Field>}
    <Field label="GST treatment">
      <select data-testid={`${t}-gst-treatment`} className={SEL} disabled={lock} value={fee.gst_treatment} onChange={(e) => save({ gst_treatment: e.target.value })}>
        {Object.entries(GST_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select></Field>
  </>;
}

const SRC = { override: "admin override", signed_contract: "signed contract", signed_contract_wording: "signed contract ($150/week wording)" };
function FeeSection({ fee, editable, hid, apply }) {
  const initial = fee.fee_type === "initial", t = initial ? "acct-initial" : "acct-final";
  const [busy, setBusy] = useState(false);
  const run = (p) => { setBusy(true); return apply(p).finally(() => setBusy(false)); };
  const save = (body) => run(updateFee(hid, fee.fee_type, body));
  const sale = (patch) => {
    const b = { outcome: fee.sale_outcome, sale_price: fee.sale_price, currency: fee.currency || undefined, sold_at: fee.sale_date || undefined, ...patch };
    if (b.outcome !== "sold") b.sale_price = null;
    return run(saveSale(hid, b));
  };
  const off = !editable || busy;
  const gst = fee.gst_treatment === "needs_review" ? "Needs review" : fee.gst == null ? "—" : `${money(fee.gst, fee.currency)} (${fee.gst_rate}%)`;
  return (
    <Section title={initial ? "A. Initial Advertising Fee" : "B. Final Advertising Fee"} testId={t}
      action={<span data-testid={`${t}-status-badge`} className="text-xs font-bold text-stone-700">{busy ? "Saving…" : INVOICE_LABEL[fee.invoice_status]}</span>}>
      {initial ? <>
        <Row k="Advertising duration" v={fee.weeks ? `${fee.weeks} week${fee.weeks === 1 ? "" : "s"} (signed contract)` : null} testId={`${t}-weeks`} />
        <Row k="Rate" v={fee.rate != null ? `${money(fee.rate, fee.currency)} / week · ${SRC[fee.rate_source] || ""}` : null} testId={`${t}-rate`} />
      </> : <Row k="Fee % (signed contract)" v={fee.percent != null ? `${fee.percent}%` : null} testId={`${t}-percent`} />}
      <Row k="Subtotal" v={money(fee.subtotal, fee.currency)} testId={`${t}-subtotal`} />
      <Row k={fee.gst_treatment === "gst" ? "GST" : `GST · ${GST_LABEL[fee.gst_treatment]}`} v={gst} testId={`${t}-gst`} />
      <Row k="Total" v={money(fee.total, fee.currency)} testId={`${t}-total`} strong />
      {fee.locked && <p data-testid={`${t}-locked`} className="mt-2 flex items-center gap-1.5 text-xs text-stone-700 bg-stone-50 border rounded-lg p-2"><Lock className="h-3.5 w-3.5" />Invoiced amounts are locked. Void the invoice to change pricing, GST or the sale.</p>}
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <PricingFields fee={fee} t={t} off={off} save={save} sale={sale} />
        <InvoiceFields fee={fee} t={t} off={off} save={save} />
        <PaymentFields fee={fee} t={t} off={off} save={save} />
        <Field label="Notes" wide><SaveInput testId={`${t}-notes-input`} disabled={off} value={fee.notes} onSave={(v) => save({ notes: v })} /></Field>
      </div>
      <p className="mt-2 text-[11px] text-stone-500">Changes save automatically and are kept in the accounting history. Nothing is sent to QuickBooks.</p>
      {fee.needs_review && <p data-testid={`${t}-needs-review`} className="mt-2 text-xs font-bold text-orange-900">Pricing needs admin review.</p>}
      <Notes list={fee.notes_list} testId={`${t}-notes`} />
    </Section>
  );
}

const BLANK = { category: "facebook_ads", description: "", amount: "", currency: "CAD", notes: "" };
function ExpenseDialog({ hid, exp, cur, onClose, onSaved }) {
  const [f, setF] = useState(exp?.id ? { ...exp, notes: exp.notes || "" } : { ...BLANK, date: today(), currency: cur || "CAD" });
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const save = async () => {
    const body = { date: f.date, category: f.category, description: f.description, amount: Number(f.amount), currency: f.currency, notes: f.notes };
    try { await (exp?.id ? editExpense(exp.id, body) : addExpense(hid, body)); toast.success("Expense saved"); onSaved(); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="expense-dialog" className="max-w-md">
        <DialogHeader><DialogTitle>{exp?.id ? "Edit expense" : "Add expense"}</DialogTitle><DialogDescription>Advertising cost for this horse (excluding GST).</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={L}>Date</label><Input data-testid="expense-date" type="date" value={f.date} onChange={(e) => set({ date: e.target.value })} /></div>
          <div><label className={L}>Platform / category</label>
            <select data-testid="expense-category" className={SEL} value={f.category} onChange={(e) => set({ category: e.target.value })}>
              {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <div className="col-span-2"><label className={L}>Description</label><Input data-testid="expense-description" value={f.description} onChange={(e) => set({ description: e.target.value })} /></div>
          <div><label className={L}>Amount</label><Input data-testid="expense-amount" type="number" min="0" step="0.01" value={f.amount} onChange={(e) => set({ amount: e.target.value })} /></div>
          <div><label className={L}>Currency</label>
            <select data-testid="expense-currency" className={SEL} value={f.currency} onChange={(e) => set({ currency: e.target.value })}><option>CAD</option><option>USD</option></select></div>
          <div className="col-span-2"><label className={L}>Notes</label><Input data-testid="expense-notes" value={f.notes} onChange={(e) => set({ notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="expense-save" onClick={save}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Expenses({ a, reload }) {
  const [edit, setEdit] = useState(null);
  const del = async (e) => { if (!window.confirm(`Delete expense "${e.description}"?`)) return; try { await deleteExpense(e.id); toast.success("Expense deleted"); reload(); } catch (x) { toast.error(errText(x)); } };
  return (
    <Section title="C. Advertising Expenses" testId="acct-expenses"
      action={a.editable && <Button data-testid="expense-add" size="sm" variant="outline" className="rounded-full h-8" onClick={() => setEdit({})}><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>}>
      {!a.expenses.length ? <p className="text-sm text-stone-600">No expenses recorded.</p> : (
        <ul className="divide-y divide-stone-100">
          {a.expenses.map((e, i) => (
            <li key={e.id} data-testid={`expense-${i}`} className="py-2 flex items-start gap-2 text-sm">
              <div className="min-w-0 flex-1"><div className="font-medium text-stone-900 break-words">{e.description}</div>
                <div className="text-xs text-stone-600">{d8(e.date)} · {CATEGORY_LABEL[e.category]}{e.notes ? ` · ${e.notes}` : ""}</div></div>
              <span data-testid={`expense-${i}-amount`} className="font-semibold shrink-0">{money(e.amount, e.currency)}</span>
              {a.editable && <>
                <button data-testid={`expense-${i}-edit`} onClick={() => setEdit(e)} aria-label="Edit expense" className="p-1 text-stone-600 hover:text-stone-900"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`expense-${i}-delete`} onClick={() => del(e)} aria-label="Delete expense" className="p-1 text-red-700 hover:text-red-900"><Trash2 className="h-4 w-4" /></button>
              </>}
            </li>
          ))}
        </ul>
      )}
      {edit && <ExpenseDialog hid={a.horse_id} exp={edit} cur={a.contract?.currency} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </Section>
  );
}

function Summary({ s }) {
  const curs = Object.keys(s);
  return (
    <Section title="D. Financial Summary" testId="acct-summary">
      <p className="text-xs text-stone-600 mb-2">All amounts exclude GST except Total invoiced, paid and outstanding. Currencies are never converted.</p>
      {!curs.length && <p className="text-sm text-stone-600">Nothing to summarise yet.</p>}
      {curs.map((c) => { const r = s[c]; const t = `acct-summary-${c}`; return (
        <div key={c} data-testid={t} className="mb-3 last:mb-0">
          {curs.length > 1 && <p className="text-xs font-bold text-stone-700 mt-1">{c}</p>}
          <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mt-1">Expected (calculated)</p>
          <Row k="Initial advertising revenue" v={money(r.expected_initial, c)} testId={`${t}-initial`} />
          <Row k="Final advertising revenue" v={money(r.expected_final, c)} testId={`${t}-final`} />
          <Row k="Additional service revenue" v={money(r.additional, c)} testId={`${t}-additional`} />
          <Row k="Total revenue" v={money(r.total_revenue, c)} testId={`${t}-revenue`} strong />
          <Row k="Total advertising expenses" v={money(r.expenses, c)} testId={`${t}-expenses`} />
          <Row k="Contribution before other expenses" v={money(r.contribution, c)} testId={`${t}-contribution`} strong />
          <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mt-2">Invoiced & collected</p>
          <Row k="Invoiced revenue (excl. GST)" v={money(r.invoiced_revenue, c)} testId={`${t}-invoiced-revenue`} />
          <Row k="Total invoiced (incl. GST)" v={money(r.total_invoiced, c)} testId={`${t}-invoiced`} />
          <Row k="Total paid" v={money(r.total_paid, c)} testId={`${t}-paid`} />
          <Row k="Total outstanding" v={money(r.outstanding, c)} testId={`${t}-outstanding`} strong />
        </div>
      ); })}
    </Section>
  );
}

// Plain checklist of what this horse still needs before each fee can be calculated.
function Missing({ a }) {
  const f = a.final;
  const list = [
    !a.has_application && "Online application — this horse was imported without one, so there is no signed contract to price from.",
    a.has_application && !a.contract && "Signed contract — the seller has not signed yet (sets currency, weeks and final fee %).",
    a.contract && !a.contract.currency && "Contract currency.",
    a.contract && !a.contract.weeks && "Contract advertising weeks.",
    f.sale_outcome !== "sold" && "Sale outcome — set the Final Advertising Fee's Sale outcome to Sold.",
    f.sale_outcome === "sold" && f.sale_price == null && "Confirmed sale price.",
  ].filter(Boolean);
  if (!list.length) return null;
  return (
    <section data-testid="acct-missing" className="bg-amber-50 border border-amber-200 rounded-xl p-3">
      <p className="text-sm font-semibold text-amber-900">Missing for full accounting</p>
      <ul className="mt-1 list-disc pl-5 text-xs text-amber-900 space-y-0.5">{list.map((t) => <li key={t}>{t}</li>)}</ul>
    </section>
  );
}

export function WorkspaceAccounting({ hid }) {
  const [a, setA] = useState(null);
  const [rev, setRev] = useState(0);
  const load = () => getHorseAccounting(hid).then(setA).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); }, [hid]); // eslint-disable-line react-hooks/exhaustive-deps
  // Every inline save returns the fresh accounting view; on a rejected change, reload and reset the inputs.
  const apply = (p) => p.then((d) => { setA(d); toast.success("Saved"); }).catch((e) => { toast.error(errText(e)); setRev((r) => r + 1); return load(); });
  if (!a) return <p className="text-sm text-stone-600 p-2">Loading accounting…</p>;
  return (
    <div data-testid="workspace-accounting" className="space-y-3">
      {!a.is_test && <p data-testid="acct-live-note" className="flex items-center gap-1.5 text-xs text-stone-700 bg-white border border-stone-200 rounded-lg p-2"><Lock className="h-3.5 w-3.5" />LIVE horse — changes here are real accounting records (no QuickBooks connection yet).</p>}
      <p data-testid="acct-contract" className="text-xs text-stone-600">{a.contract ? `Signed contract · ${a.contract.currency || "no currency"} · ${a.contract.country || ""} · ${a.contract.weeks} week(s) · ${a.contract.fee_percent}% final fee` : a.has_application ? "No signed contract yet — fees are calculated once the seller signs." : "Imported horse — no online contract, so fees can only be set by hand (rate override)."}</p>
      <Missing a={a} />
      <FeeSection key={`i${rev}`} fee={a.initial} editable={a.editable} hid={hid} apply={apply} />
      <FeeSection key={`f${rev}`} fee={a.final} editable={a.editable} hid={hid} apply={apply} />
      <Expenses a={a} reload={load} />
      <Summary s={a.summary} />
      {!!a.history.length && (
        <details data-testid="acct-history" className="bg-white border border-stone-200 rounded-xl p-4 text-sm">
          <summary className="font-semibold cursor-pointer">Accounting history ({a.history.length})</summary>
          <ul className="mt-2 space-y-1 text-xs text-stone-700">{a.history.map((h) => <li key={h.id}>{d8(h.at.slice(0, 10))} · {h.by} · {h.entity.replace("_", " ")} {h.action}{Object.keys(h.changes || {}).length ? `: ${Object.keys(h.changes).join(", ")}` : ""}</li>)}</ul>
        </details>
      )}
    </div>
  );
}
